import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './InterviewScheduler.css';
import { useSettings } from '../../contexts/SettingsContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getMetadata } from '../../utils/encryptedStorage';
import aiAutomationApi from '../../utils/aiAutomationApi';
import InputBox from '../InputBox';
import Toast from '../Toast';
import AIInterviewConfigSelector from './AIInterviewConfigSelector';
import InterviewTypeChoice from './InterviewTypeChoice';
import { canUseCompanyFeature } from '../../utils/companyFeatureAccess';
import { calculateTimeSlots, assignCandidatesToSlots, calculateMaxCandidates, suggestEndTime } from './utils/slotCalculator';
import { formatTime, formatDate, formatDateDisplay, formatDateToYYYYMMDD } from './utils/dateUtils';

/**
 * InterviewScheduler Component
 * Multi-step interview scheduling with Google Calendar integration
 * Supports dark/light mode via ThemeContext
 */
const InterviewScheduler = ({
  candidates = [],
  onScheduleComplete,
  isOpen = false,
  onClose,
  searchId = null,
  jobTitle = '',
  enableInterviewTypeChoice = false,
  companyId = '',
  sourceSessionId = '',
  initialInterviewSetup = null,
  featureAccess = null,
  alwaysShowInterviewTypeChoice = false,
  initialInterviewMode = '',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useSettings();
  const { isDark } = useTheme();
  const resolvedCompanyId = String(companyId || getMetadata()?.cid || '');
  const primaryColor = settings?.theme?.colors?.primary || '#6366f1';
  const secondaryColor = settings?.theme?.colors?.secondary || '#8b5cf6';
  const canUseAiInterview = canUseCompanyFeature(featureAccess, 'ai_interview');
  const canUseManualInterview = canUseCompanyFeature(featureAccess, 'manual_interview');
  const isInterviewModeEnabled = useCallback((mode) => (
    (mode === 'ai' && canUseAiInterview)
    || (mode === 'manual' && canUseManualInterview)
  ), [canUseAiInterview, canUseManualInterview]);
  const getAllowedInitialMode = useCallback((mode) => (isInterviewModeEnabled(mode) ? mode : ''), [isInterviewModeEnabled]);
  const defaultInterviewMode = enableInterviewTypeChoice && alwaysShowInterviewTypeChoice
    ? getAllowedInitialMode(initialInterviewMode)
    : enableInterviewTypeChoice
    ? (!canUseAiInterview && canUseManualInterview ? 'manual' : canUseAiInterview && !canUseManualInterview ? 'ai' : '')
    : (canUseManualInterview ? 'manual' : canUseAiInterview ? 'ai' : '');

  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const [interviewMode, setInterviewMode] = useState(defaultInterviewMode);
  const [interviewSetup, setInterviewSetup] = useState(null);
  const [selectedAiConfig, setSelectedAiConfig] = useState(null);
  const [setupLoading, setSetupLoading] = useState(false);
  useEffect(() => {
    setInterviewMode((prev) => {
      if (prev === 'ai' && !canUseAiInterview && canUseManualInterview) return 'manual';
      if (prev === 'manual' && !canUseManualInterview && canUseAiInterview) return 'ai';
      if (prev && !isInterviewModeEnabled(prev)) return defaultInterviewMode || '';
      if (!prev && defaultInterviewMode) return defaultInterviewMode;
      return prev;
    });
  }, [canUseAiInterview, canUseManualInterview, defaultInterviewMode, isInterviewModeEnabled]);
  const isAiMode = interviewMode === 'ai';
  const steps = [
    { id: 1, label: 'Setup', description: isAiMode ? 'AI config & candidates' : 'Interviewer & candidates' },
    { id: 2, label: 'Availability', description: 'Dates & time slots' },
    { id: 3, label: 'Review & Send', description: 'Confirm & schedule' },
    { id: 4, label: 'Confirmation', description: 'Done!' },
  ];

  // Google Calendar connection state
  const [isCalendarConnected, setIsCalendarConnected] = useState(false);
  const [isCheckingCalendar, setIsCheckingCalendar] = useState(true);
  const [isCalendarConfigured, setIsCalendarConfigured] = useState(true);

  // Form state - initialize title with jobTitle if provided
  const [availability, setAvailability] = useState({
    title: jobTitle || '',
    dates: [],
    startTime: '09:00',
    endTime: '17:00',
    interviewDuration: 30,
    bufferTime: 0,
  });

  // Update title when jobTitle prop changes
  useEffect(() => {
    if (jobTitle) {
      setAvailability(prev => {
        if (!prev.title) {
          return { ...prev, title: jobTitle };
        }
        return prev;
      });
    }
  }, [jobTitle]);

  // Existing slots state
  const [existingSlots, setExistingSlots] = useState(null);
  const [loadingExistingSlots, setLoadingExistingSlots] = useState(false);
  // Map of email → { booking_status, name, scheduled_date, scheduled_time }
  const [alreadyScheduledCandidates, setAlreadyScheduledCandidates] = useState(new Set());
  const [candidateStatusMap, setCandidateStatusMap] = useState({});
  const [loadingCandidatesStatus, setLoadingCandidatesStatus] = useState(false);
  // Counts from backend: booked vs pending among already-scheduled
  const [scheduledCounts, setScheduledCounts] = useState({ booked: 0, pending: 0 });
  const [lockedDates, setLockedDates] = useState([]);

  // Global slots state - shows slots from ALL searches
  const [globalSlots, setGlobalSlots] = useState(null);
  const [loadingGlobalSlots, setLoadingGlobalSlots] = useState(false);

  const [selectedDates, setSelectedDates] = useState([]);
  const [scheduledCandidates, setScheduledCandidates] = useState([]);
  const [scheduledResult, setScheduledResult] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [isScheduling, setIsScheduling] = useState(false);
  const [invitationResults, setInvitationResults] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [toastState, setToastState] = useState({ visible: false, message: '', type: 'error' });
  const showToastMsg = useCallback((message, type = 'error') => {
    setToastState({ visible: true, message, type });
  }, []);
  const [showDateModal, setShowDateModal] = useState(false);
  const [deletedCandidateEmails, setDeletedCandidateEmails] = useState(new Set());

  // Interviewer state
  const [interviewers, setInterviewers] = useState([]);
  const [selectedInterviewer, setSelectedInterviewer] = useState('');
  const [interviewersLoading, setInterviewersLoading] = useState(false);
  const [showNoInterviewerModal, setShowNoInterviewerModal] = useState(false);

  // Track whether user has manually adjusted the end time
  const [userManuallySetEndTime, setUserManuallySetEndTime] = useState(false);
  // Track not-enough-slots warning (non-blocking)
  const [slotWarning, setSlotWarning] = useState(null);
  // Whether the "add more slots" creation panel is expanded
  const [showAddMoreSlots, setShowAddMoreSlots] = useState(false);

  // Setup step: which candidate emails are selected (Set of lowercase emails)
  const [setupSelectedEmails, setSetupSelectedEmails] = useState(new Set());
  // Collapse state for Setup step sections
  const [showBookedSection, setShowBookedSection] = useState(false);
  const [showPendingSection, setShowPendingSection] = useState(false);
  // Previous interviewer from existing sessions (to detect mismatch)
  const [previousInterviewerId, setPreviousInterviewerId] = useState(null);
  // Backend slot validation
  const [validationResult, setValidationResult] = useState(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const manualInterviewers = useMemo(
    () => interviewers.filter((interviewer) => !String(interviewer.id || '').startsWith('ai-interviewer-')),
    [interviewers],
  );
  const navigateToInterviewerSettings = useCallback(() => {
    if (sourceSessionId) {
      window.sessionStorage.setItem(`return-to-schedule-modal:${sourceSessionId}`, JSON.stringify({ mode: 'manual' }));
    }
    const params = new URLSearchParams(location.search);
    params.set('schedule_modal', 'manual');
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    navigate('/account/settings', { state: { fromScheduleInterview: true } });
  }, [location.pathname, location.search, navigate, sourceSessionId]);
  useEffect(() => {
    if (isAiMode) return;
    if (selectedInterviewer && !manualInterviewers.some((interviewer) => String(interviewer.id) === String(selectedInterviewer))) {
      setSelectedInterviewer('');
    }
  }, [isAiMode, manualInterviewers, selectedInterviewer]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (enableInterviewTypeChoice && initialInterviewSetup?.exists) {
      if (!isInterviewModeEnabled(initialInterviewSetup.interview_type || 'manual')) {
        setInterviewSetup(null);
        setInterviewMode(defaultInterviewMode);
        setSetupLoading(false);
        return;
      }
      setInterviewSetup(initialInterviewSetup);
      setInterviewMode(initialInterviewSetup.interview_type || 'manual');
      if (initialInterviewSetup.ai_interviewer_id) {
        setSelectedInterviewer(initialInterviewSetup.ai_interviewer_id);
        setInterviewers(prev => prev.some(i => i.id === initialInterviewSetup.ai_interviewer_id)
          ? prev
          : [{ id: initialInterviewSetup.ai_interviewer_id, name: 'Wiza AI Interviewer', email: 'AI scheduling key' }, ...prev]);
      }
      if (initialInterviewSetup.interview_type === 'ai' && initialInterviewSetup.ai_configuration_id) {
        setSelectedAiConfig({ id: initialInterviewSetup.ai_configuration_id, ...(initialInterviewSetup.configuration || {}) });
      }
      setSetupLoading(false);
      return;
    }
    if (!enableInterviewTypeChoice) {
      setInterviewMode(defaultInterviewMode || 'manual');
      return;
    }
    if (!searchId) {
      setInterviewSetup(null);
      setInterviewMode(defaultInterviewMode);
      return;
    }
    if (alwaysShowInterviewTypeChoice) {
      setInterviewSetup(null);
      setInterviewMode(defaultInterviewMode);
      setSetupLoading(false);
      return;
    }

    let cancelled = false;
    const loadSetup = async () => {
      setSetupLoading(true);
      try {
        const qs = resolvedCompanyId ? `?company_id=${encodeURIComponent(resolvedCompanyId)}` : '';
        const data = await aiAutomationApi.get(`/interviews/search/${searchId}/setup${qs}`);
        if (cancelled) return;
        if (data?.exists) {
          if (!isInterviewModeEnabled(data.interview_type || 'manual')) {
            setInterviewSetup(null);
            setInterviewMode(defaultInterviewMode);
            return;
          }
          setInterviewSetup(data);
          setInterviewMode(data.interview_type || 'manual');
          if (data.ai_interviewer_id) {
            setSelectedInterviewer(data.ai_interviewer_id);
            setInterviewers(prev => prev.some(i => i.id === data.ai_interviewer_id)
              ? prev
              : [{ id: data.ai_interviewer_id, name: 'Wiza AI Interviewer', email: 'AI scheduling key' }, ...prev]);
          }
          if (data.interview_type === 'ai' && data.ai_configuration_id) {
            setSelectedAiConfig({ id: data.ai_configuration_id, ...(data.configuration || {}) });
          }
        } else {
          setInterviewSetup(null);
          setInterviewMode(defaultInterviewMode);
        }
      } catch (err) {
        console.error('Failed to load interview setup:', err);
        if (!cancelled) setInterviewMode(defaultInterviewMode);
      } finally {
        if (!cancelled) setSetupLoading(false);
      }
    };
    loadSetup();
    return () => { cancelled = true; };
  }, [isOpen, enableInterviewTypeChoice, searchId, resolvedCompanyId, initialInterviewSetup, defaultInterviewMode, isInterviewModeEnabled, alwaysShowInterviewTypeChoice, initialInterviewMode]);

  const createInterviewSetup = useCallback(async (mode, config = null) => {
    if (!searchId) {
      setInterviewMode(mode);
      return null;
    }
    setSetupLoading(true);
    try {
      if (!resolvedCompanyId) throw new Error('Company context is unavailable. Please sign in again.');
      const data = await aiAutomationApi.post(`/interviews/search/${searchId}/setup`, {
        company_id: resolvedCompanyId,
        session_id: String(sourceSessionId || ''),
        interview_type: mode,
        ai_configuration_id: mode === 'ai' ? (config?.id || null) : null,
        setup_snapshot: mode === 'ai' ? (config || {}) : {},
      });
      setInterviewSetup(data);
      setInterviewMode(data.interview_type || mode);
      if (data.ai_interviewer_id) {
        setSelectedInterviewer(data.ai_interviewer_id);
        setInterviewers(prev => prev.some(i => i.id === data.ai_interviewer_id)
          ? prev
          : [{ id: data.ai_interviewer_id, name: 'Wiza AI Interviewer', email: 'AI scheduling key' }, ...prev]);
      }
      return data;
    } catch (err) {
      showToastMsg(err?.response?.data?.detail || err?.detail || err.message || 'Failed to create interview setup.', 'error');
      return null;
    } finally {
      setSetupLoading(false);
    }
  }, [searchId, resolvedCompanyId, sourceSessionId, showToastMsg]);

  // Check Google Calendar connection status
  useEffect(() => {
    const checkCalendarConnection = async () => {
      if (!isOpen) return;
      
      setIsCheckingCalendar(true);
      try {
        const data = await aiAutomationApi.get('/interviews/calendar/status');
        setIsCalendarConnected(data?.connected || false);
        setIsCalendarConfigured(data?.configured !== false);
      } catch (err) {
        console.error('Error checking calendar connection:', err);
        setIsCalendarConnected(false);
      } finally {
        setIsCheckingCalendar(false);
      }
    };

    if (isOpen) {
      checkCalendarConnection();
    }
  }, [isOpen]);

  // Fetch active interviewers on open
  useEffect(() => {
    if (!isOpen) return;
    const fetchInterviewers = async () => {
      const metadata = getMetadata();
      const userId = metadata?.uid || metadata?.userId || metadata?.id;
      if (!userId) return;
      setInterviewersLoading(true);
      try {
        const data = await aiAutomationApi.get(`/interviewers?user_id=${userId}&active_only=true`);
        const list = data.interviewers || [];
        setInterviewers(prev => [
          ...prev.filter(i => String(i.id || '').startsWith('ai-interviewer-') && !list.some(item => item.id === i.id)),
          ...list,
        ]);
        // Only auto-select first interviewer if no session interviewer will be loaded
        if (list.length > 0 && !searchId) {
          setSelectedInterviewer(prev => prev || list[0].id);
        }
      } catch (err) {
        console.error('Failed to load interviewers:', err);
      } finally {
        setInterviewersLoading(false);
      }
    };
    fetchInterviewers();
  }, [isOpen, searchId]);

  // Fetch existing slots and session data when searchId is provided
  useEffect(() => {
    const fetchExistingSession = async () => {
      if (!isOpen || !searchId) return;
      
      setLoadingExistingSlots(true);
      try {
        const slotsData = await aiAutomationApi.get(`/interviews/search/${searchId}/slots`);
        setExistingSlots(slotsData);
        
        // Collect all locked dates from existing sessions (future dates only)
        const allDates = new Set();
        const todayStr = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
        if (slotsData.sessions && slotsData.sessions.length > 0) {
          slotsData.sessions.forEach(session => {
            if (session.dates && Array.isArray(session.dates)) {
              session.dates.forEach(date => {
                // Only include dates that are today or in the future
                if (date >= todayStr) {
                  allDates.add(date);
                }
              });
            }
          });
        }
        setLockedDates(Array.from(allDates));
        
        // Pre-populate form with existing session data
        if (slotsData.sessions && slotsData.sessions.length > 0) {
          const firstSession = slotsData.sessions[0];
          const lockedDatesArray = Array.from(allDates);
          
          setAvailability(prev => {
            const mergedDates = [...new Set([...lockedDatesArray, ...(prev.dates || [])])].sort();
            return {
              ...prev,
              title: firstSession.title || prev.title,
              interviewDuration: slotsData.duration || prev.interviewDuration,
              bufferTime: slotsData.buffer_time || 0,
              dates: mergedDates,
              startTime: firstSession.start_time || prev.startTime,
              endTime: firstSession.end_time || prev.endTime,
            };
          });
          
          // Set the interviewer from the session if available
          if (firstSession.interviewer_id) {
            setSelectedInterviewer(firstSession.interviewer_id);
            setPreviousInterviewerId(firstSession.interviewer_id);
          }
          
          // Update selectedDates with locked dates
          if (lockedDatesArray.length > 0) {
            const lockedDateObjects = lockedDatesArray.map(dateStr => {
              try {
                const [year, month, day] = dateStr.split('-').map(Number);
                return new Date(year, month - 1, day);
              } catch (e) {
                return new Date(dateStr);
              }
            });
            setSelectedDates(prev => {
              const existingDateStrs = prev.map(d => formatDateToYYYYMMDD(d));
              const newLockedDates = lockedDateObjects.filter(d => {
                const dateStr = formatDateToYYYYMMDD(d);
                return !existingDateStrs.includes(dateStr);
              });
              return [...prev, ...newLockedDates].sort((a, b) => a - b);
            });
          }
        }
        
        // Fetch candidates status (already scheduled candidates) with booking_status
        try {
          setLoadingCandidatesStatus(true);
          const candidatesData = await aiAutomationApi.get(`/interviews/search/${searchId}/candidates-status`);
          const alreadyScheduledSet = new Set();
          const statusMap = {};
          Object.entries(candidatesData.already_scheduled_candidates || {}).forEach(([email, info]) => {
            const key = email.toLowerCase();
            alreadyScheduledSet.add(key);
            statusMap[key] = {
              name: info.name,
              email: info.email,
              booking_status: info.booking_status || 'pending',
              scheduled_date: info.scheduled_date || null,
              scheduled_time: info.scheduled_time || null,
              interviewer_name: info.interviewer_name || null,
            };
          });
          setAlreadyScheduledCandidates(alreadyScheduledSet);
          setCandidateStatusMap(statusMap);
          setScheduledCounts({
            booked: candidatesData.booked_count || 0,
            pending: candidatesData.pending_count || 0,
          });
        } catch (candidatesErr) {
          console.error('Error fetching candidates status:', candidatesErr);
          setAlreadyScheduledCandidates(new Set());
          setCandidateStatusMap({});
          setScheduledCounts({ booked: 0, pending: 0 });
        } finally {
          setLoadingCandidatesStatus(false);
        }
      } catch (err) {
        console.error('Error fetching existing session:', err);
        setExistingSlots(null);
      } finally {
        setLoadingExistingSlots(false);
      }
    };

    if (isOpen && searchId) {
      fetchExistingSession();
    }
  }, [isOpen, searchId]);

  // Fetch global slots (across all searches) when scheduler opens or interviewer changes
  useEffect(() => {
    const fetchGlobalSlots = async () => {
      if (!isOpen) return;

      setLoadingGlobalSlots(true);
      try {
        // Pass interviewer_id to only show slots for the selected interviewer
        const url = selectedInterviewer
          ? `/interviews/slots/global?interviewer_id=${selectedInterviewer}`
          : '/interviews/slots/global';
        const data = await aiAutomationApi.get(url);
        setGlobalSlots(data);
      } catch (err) {
        console.error('Error fetching global slots:', err);
        setGlobalSlots(null);
      } finally {
        setLoadingGlobalSlots(false);
      }
    };

    if (isOpen) {
      fetchGlobalSlots();
    }
  }, [isOpen, selectedInterviewer]);

  // Pre-populate candidates from props.
  // Re-populate when `candidates` prop changes and modal is open,
  // unless the user has manually deleted candidates.
  // NOTE: `isOpen` is included so that after the reset-on-close effect
  // clears scheduledCandidates, re-opening the modal triggers a fresh
  // population even when `candidates` reference hasn't changed.
  useEffect(() => {
    if (!isOpen) return;
    if (!candidates || candidates.length === 0) return;
    // Only auto-populate when no user deletions have occurred
    if (deletedCandidateEmails.size > 0) return;

    const prePopulated = candidates
      .filter(c => {
        const email = (c.email || c.candidate_email || '').trim().toLowerCase();
        return email && !deletedCandidateEmails.has(email);
      })
      .map(c => {
        const candidateName = c.name || c.candidate_name || c.full_name || '';
        const displayName = candidateName || (c.email ? c.email.split('@')[0] : 'Candidate');
        
        return {
          name: displayName,
          email: c.email || c.candidate_email || '',
          phone: c.phone || c.candidate_phone || '',
          preferredDates: [],
          preferredStartTime: '',
          preferredEndTime: '',
          assignedSlot: null,
          sourceCandidateId: c.id || c.candidate_id || c.candidateId || '',
        };
      });
    setScheduledCandidates(prePopulated);
  }, [candidates, deletedCandidateEmails.size, isOpen]);

  // Auto-select all active candidates in Setup step when candidates populate
  useEffect(() => {
    if (!isOpen || scheduledCandidates.length === 0) return;
    setSetupSelectedEmails(prev => {
      if (prev.size > 0) return prev; // Don't override user selections
      const emails = new Set();
      scheduledCandidates.forEach(c => {
        const email = (c.email || '').trim().toLowerCase();
        if (email && !alreadyScheduledCandidates.has(email)) {
          emails.add(email);
        }
      });
      return emails;
    });
  }, [isOpen, scheduledCandidates, alreadyScheduledCandidates]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(1);
      setAvailability({
        title: '',
        dates: [],
        startTime: '09:00',
        endTime: '17:00',
        interviewDuration: 30,
        bufferTime: 0,
      });
      setSelectedDates([]);
      setScheduledCandidates([]);
      setScheduledResult(null);
      setSessionId(null);
      setInvitationResults(null);
      setError(null);
      setSuccessMessage(null);
      setShowDateModal(false);
      setDeletedCandidateEmails(new Set());
      setGlobalSlots(null);
      setInterviewers([]);
      setSelectedInterviewer('');
      setShowNoInterviewerModal(false);
      setUserManuallySetEndTime(false);
      setSlotWarning(null);
      setShowAddMoreSlots(false);
      setSetupSelectedEmails(new Set());
      setValidationResult(null);
      setValidationLoading(false);
      setCandidateStatusMap({});
      setScheduledCounts({ booked: 0, pending: 0 });
      setShowBookedSection(false);
      setShowPendingSection(false);
      setPreviousInterviewerId(null);
      setInterviewMode(defaultInterviewMode);
      setInterviewSetup(null);
      setSelectedAiConfig(null);
      setSetupLoading(false);
    }
  }, [isOpen, defaultInterviewMode]);

  // Compute OTHER JOBS' slots (for overlap blocking) — exclude current job's slots
  const globalOccupiedSlots = useMemo(() => {
    if (!globalSlots) return { byDate: {}, set: new Set(), bookedCount: 0, availableCount: 0, totalCount: 0, datesWithSlots: [] };

    const todayStr = new Date().toISOString().split('T')[0];
    const slotSet = new Set();
    const byDate = {};
    let blockedCount = 0;

    // Only include slots from OTHER jobs (not current searchId)
    const allSlots = [
      ...(globalSlots.booked_slots || []).map(s => ({ ...s, status: 'booked' })),
      ...(globalSlots.available_slots || []).map(s => ({ ...s, status: 'available' })),
    ];

    for (const s of allSlots) {
      if (!s.date || !s.time || s.date < todayStr) continue;
      // Skip current job's slots — those belong in the Slot Preview, not here
      if (s.search_id === searchId) continue;

      slotSet.add(`${s.date}_${s.time}`);
      blockedCount++;

      if (!byDate[s.date]) byDate[s.date] = { blocked: [] };
      byDate[s.date].blocked.push(s.time);
    }

    return {
      byDate,
      set: slotSet,
      blockedCount,
      // Keep these for backward compat but they now only count OTHER jobs
      bookedCount: blockedCount,
      availableCount: 0,
      totalCount: blockedCount,
      datesWithSlots: Object.keys(byDate),
    };
  }, [globalSlots, searchId]);

  // Calculate max candidates
  const maxCandidates = useMemo(() => {
    if (!availability.dates.length) return 0;
    const availabilityForCalc = {
      ...availability,
      bufferTime: typeof availability.bufferTime === 'number' ? availability.bufferTime : 
                  (availability.bufferTime === '' || availability.bufferTime === null || availability.bufferTime === undefined) ? 0 : 
                  parseInt(availability.bufferTime, 10) || 0
    };
    return calculateMaxCandidates(availabilityForCalc);
  }, [availability]);

  // Calculate all available slot sessions for display
  const calculatedSlotSessions = useMemo(() => {
    if (!availability.dates.length) return [];
    const availabilityForCalc = {
      ...availability,
      bufferTime: typeof availability.bufferTime === 'number' ? availability.bufferTime : 
                  (availability.bufferTime === '' || availability.bufferTime === null || availability.bufferTime === undefined) ? 0 : 
                  parseInt(availability.bufferTime, 10) || 0
    };
    return calculateTimeSlots(availabilityForCalc);
  }, [availability]);

  // Determine which calculated slots will be skipped vs created
  const slotConflictInfo = useMemo(() => {
    if (!calculatedSlotSessions.length) return { newSlots: [], conflictSlots: [], newCount: 0, conflictCount: 0 };
    const occupiedSet = globalOccupiedSlots.set || new Set();
    const newSlots = [];
    const conflictSlots = [];
    for (const slot of calculatedSlotSessions) {
      const key = `${slot.date}_${slot.startTime}`;
      if (occupiedSet.has(key)) {
        conflictSlots.push(slot);
      } else {
        newSlots.push(slot);
      }
    }
    return { newSlots, conflictSlots, newCount: newSlots.length, conflictCount: conflictSlots.length };
  }, [calculatedSlotSessions, globalOccupiedSlots.set]);

  // Slot counts specifically for THIS job/search_id (not cross-job global slots)
  const jobAvailableCount = useMemo(() => {
    if (!existingSlots) return 0;
    return existingSlots?.stats?.available ?? (existingSlots?.available_slots || []).length;
  }, [existingSlots]);

  const jobBookedCount = useMemo(() => {
    if (!existingSlots) return 0;
    return existingSlots?.stats?.booked ?? (existingSlots?.booked_slots || []).length;
  }, [existingSlots]);

  const jobTotalCount = jobAvailableCount + jobBookedCount;

  // Active candidates = selected in Setup step and not already scheduled
  const activeSelectedCandidates = useMemo(() => {
    return scheduledCandidates.filter(c => {
      const email = (c.email || '').trim().toLowerCase();
      return setupSelectedEmails.has(email) && !alreadyScheduledCandidates.has(email);
    });
  }, [scheduledCandidates, setupSelectedEmails, alreadyScheduledCandidates]);

  // Backend slot validation — call when availability params change on Step 2
  useEffect(() => {
    if (currentStep !== 2) return;
    if (!selectedInterviewer || !searchId) return;
    if (!showAddMoreSlots && jobAvailableCount > 0) {
      setValidationResult(null);
      return;
    }
    if (availability.dates.length === 0) {
      setValidationResult(null);
      return;
    }
    if (availability.startTime >= availability.endTime) return;

    const controller = new AbortController();
    const validate = async () => {
      setValidationLoading(true);
      try {
        const result = await aiAutomationApi.post('/interviews/slots/validate', {
          interviewer_id: selectedInterviewer,
          search_id: searchId,
          dates: availability.dates,
          start_time: availability.startTime,
          end_time: availability.endTime,
          duration: availability.interviewDuration,
          buffer_time: availability.bufferTime || 0,
          interview_type: isAiMode ? 'ai' : 'manual',
          interview_setup_id: isAiMode ? (interviewSetup?.setup_id || interviewSetup?.interview_setup_id || null) : null,
          round_id: isAiMode ? (interviewSetup?.round_id || null) : null,
        });
        if (!controller.signal.aborted) {
          setValidationResult(result);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('Slot validation failed:', err);
          setValidationResult(null);
        }
      } finally {
        if (!controller.signal.aborted) {
          setValidationLoading(false);
        }
      }
    };

    const timer = setTimeout(validate, 400);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentStep, selectedInterviewer, searchId,
    availability.dates.length, availability.startTime, availability.endTime,
    availability.interviewDuration, availability.bufferTime,
    showAddMoreSlots, jobAvailableCount,
  ]);

  // Auto-expand slot creation if existing slots are insufficient, collapse if they're enough
  useEffect(() => {
    if (loadingExistingSlots || loadingGlobalSlots) return;
    const activeCandCount = scheduledCandidates.filter(
      c => !alreadyScheduledCandidates.has(c.email?.toLowerCase())
    ).length;
    if (jobAvailableCount === 0) {
      setShowAddMoreSlots(true); // No slots at all — must create
    } else if (activeCandCount > 0 && jobAvailableCount < activeCandCount) {
      setShowAddMoreSlots(true); // Not enough — auto-expand so user adds more
    } else {
      setShowAddMoreSlots(false); // Enough slots — collapse creation UI
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobAvailableCount, loadingExistingSlots, loadingGlobalSlots]);

  // Auto-adjust end time based on candidate count when user hasn't manually set it
  useEffect(() => {
    if (userManuallySetEndTime) return; // User overrode — don't auto-adjust
    if (!availability.dates.length) return;
    if (!scheduledCandidates.length) return;

    const activeCandidateCount = scheduledCandidates.filter(
      c => !alreadyScheduledCandidates.has(c.email?.toLowerCase())
    ).length;
    if (activeCandidateCount <= 0) return;

    // When in "Add More Slots" mode and existing slots already cover all candidates,
    // don't auto-constrain the end time — the user is adding extra capacity and needs
    // a meaningful time window, not one sized to just 1 overlapping slot.
    if (showAddMoreSlots && jobAvailableCount >= activeCandidateCount) return;

    const bufferTime = typeof availability.bufferTime === 'number' ? availability.bufferTime :
      (availability.bufferTime === '' || availability.bufferTime === null || availability.bufferTime === undefined) ? 0 :
      parseInt(availability.bufferTime, 10) || 0;

    // When adding more slots, suggest end time for the number of EXTRA slots needed,
    // or at least 3 slots worth so there's a meaningful selection.
    const slotsToSuggest = showAddMoreSlots
      ? Math.max(3, activeCandidateCount - jobAvailableCount)
      : activeCandidateCount;

    const suggested = suggestEndTime(
      availability.startTime,
      slotsToSuggest,
      availability.interviewDuration,
      bufferTime,
      availability.dates.length
    );

    // Only update if the suggested end time differs from the current one
    if (suggested !== availability.endTime) {
      setAvailability(prev => ({ ...prev, endTime: suggested }));
    }
  }, [
    scheduledCandidates.length,
    alreadyScheduledCandidates,
    availability.dates.length,
    availability.startTime,
    availability.interviewDuration,
    availability.bufferTime,
    userManuallySetEndTime,
    showAddMoreSlots,
    jobAvailableCount,
  ]);

  // Calculate candidate slots
  const candidateSlots = useMemo(() => {
    if (!availability.dates.length || scheduledCandidates.length === 0) return new Map();
    
    const availabilityForCalc = {
      ...availability,
      bufferTime: typeof availability.bufferTime === 'number' ? availability.bufferTime : 
                  parseInt(availability.bufferTime, 10) || 0
    };
    const allSlots = calculateTimeSlots(availabilityForCalc);
    const slotMap = new Map();
    const usedSlots = new Set();
    
    scheduledCandidates.forEach((candidate, index) => {
      if (candidate.assignedSlot) {
        const slotKey = `${candidate.assignedSlot.date}_${candidate.assignedSlot.startTime}`;
        usedSlots.add(slotKey);
        slotMap.set(index, candidate.assignedSlot);
      }
    });
    
    const forAutoAssign = scheduledCandidates
      .map((c, i) => ({ candidate: c, index: i }))
      .filter(({ candidate }) => !candidate.assignedSlot);
    
    const availableSlots = allSlots.filter(slot => 
      !usedSlots.has(`${slot.date}_${slot.startTime}`)
    );
    
    const autoAssigned = assignCandidatesToSlots(
      forAutoAssign.map(({ candidate }) => candidate),
      availableSlots
    );
    
    let autoIndex = 0;
    forAutoAssign.forEach(({ index }) => {
      if (autoIndex < autoAssigned.length) {
        slotMap.set(index, autoAssigned[autoIndex].slot);
        autoIndex++;
      }
    });
    
    return slotMap;
  }, [availability, scheduledCandidates]);

  const scheduledInterviews = useMemo(() => {
    if (!availability.dates.length || scheduledCandidates.length === 0) return [];
    
    const interviews = scheduledCandidates
      .map((candidate, index) => {
        const slot = candidate.assignedSlot || candidateSlots.get(index);
        if (!slot) return null;
        return { candidate, slot };
      })
      .filter(Boolean);
    
    return interviews.sort((a, b) => {
      const dateCompare = a.slot.date.localeCompare(b.slot.date);
      if (dateCompare !== 0) return dateCompare;
      return a.slot.startTime.localeCompare(b.slot.startTime);
    });
  }, [availability, scheduledCandidates, candidateSlots]);

  const durationOptions = [
    { value: 15, label: '15 minutes' },
    { value: 30, label: '30 minutes' },
    { value: 45, label: '45 minutes' },
    { value: 60, label: '1 hour' },
    { value: 90, label: '1.5 hours' },
    { value: 120, label: '2 hours' },
  ];

  // Handle date selection
  const handleDateSelect = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const localDate = new Date(year, month, day, 0, 0, 0, 0);
    
    if (lockedDates.includes(dateStr)) {
      showToastMsg('This date is from a previous session and cannot be removed.', 'error');
      return;
    }
    
    setSelectedDates(prev => {
      const prevDateStrs = prev.map(d => formatDateToYYYYMMDD(d));
      const isSelected = prevDateStrs.includes(dateStr);
      
      if (isSelected) {
        const filtered = prev.filter(d => formatDateToYYYYMMDD(d) !== dateStr);
        const dateStrings = filtered.map(d => formatDateToYYYYMMDD(d));
        setAvailability(avail => ({ ...avail, dates: dateStrings }));
        return filtered;
      } else {
        const updated = [...prev, localDate].sort((a, b) => a - b);
        const dateStrings = updated.map(d => formatDateToYYYYMMDD(d));
        setAvailability(avail => ({ ...avail, dates: dateStrings }));
        return updated;
      }
    });
    setError(null);
  };

  // Handle Setup step → next (Step 1 → Step 2)
  const handleSetupNext = async () => {
    if (isAiMode && !selectedAiConfig?.id && !interviewSetup?.ai_configuration_id) {
      showToastMsg('Please select an AI interview configuration.', 'error');
      return;
    }
    if (!isAiMode && (!selectedInterviewer || manualInterviewers.length === 0)) {
      setShowNoInterviewerModal(true);
      return;
    }
    if (setupSelectedEmails.size === 0) {
      showToastMsg('Please select at least one candidate to schedule.', 'error');
      return;
    }
    if (isAiMode && !selectedInterviewer) {
      const setup = await createInterviewSetup('ai', selectedAiConfig);
      if (!setup?.ai_interviewer_id) return;
    }
    if (!interviewSetup && interviewMode === 'manual') {
      const setup = await createInterviewSetup('manual');
      if (!setup) return;
    }
    setError(null);
    setCurrentStep(2);
  };

  const handleChooseInterviewMode = (mode) => {
    if (mode === 'ai' && !canUseAiInterview) return;
    if (mode === 'manual' && !canUseManualInterview) return;
    if (mode === 'manual') {
      setInterviewMode('manual');
      setInterviewSetup(null);
      setSelectedAiConfig(null);
      setError(null);
      return;
    }
    setInterviewMode('ai');
    setError(null);
  };

  // Handle Availability step → next (Step 2 → Step 3)
  const handleAvailabilityNext = (e) => {
    e?.preventDefault();

    // Auto-set title from jobTitle if not already set
    const title = (availability.title || '').trim() || (jobTitle || '').trim();
    if (!(availability.title || '').trim() && title) {
      setAvailability(prev => ({ ...prev, title: jobTitle }));
    }

    const activeCandidateCount = activeSelectedCandidates.length;

    // If the job already has enough available slots and the user hasn't expanded slot creation,
    // skip all date/time validation — the backend will reuse existing slots.
    const needsNewSlots = showAddMoreSlots || jobAvailableCount === 0;

    if (needsNewSlots) {
      if (availability.dates.length === 0) {
        showToastMsg('Please select at least one date', 'error');
        return;
      }

      if (availability.startTime >= availability.endTime) {
        showToastMsg('Start time must be before end time. Please adjust your time range.', 'error');
        return;
      }

      const [startH, startM] = availability.startTime.split(':').map(Number);
      const [endH, endM] = availability.endTime.split(':').map(Number);
      const totalMinutes = (endH * 60 + endM) - (startH * 60 + startM);
      if (totalMinutes < availability.interviewDuration) {
        showToastMsg(`The time range (${totalMinutes} min) is shorter than the interview duration (${availability.interviewDuration} min). Please extend your availability window.`, 'error');
        return;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const futureDates = availability.dates.filter(dateStr => {
        const [y, m, d] = dateStr.split('-').map(Number);
        const date = new Date(y, m - 1, d);
        return date >= today;
      });
      if (futureDates.length === 0) {
        showToastMsg('All selected dates are in the past. Please select at least one future date.', 'error');
        return;
      }

      if (maxCandidates === 0) {
        showToastMsg('No time slots available with the selected time range and duration. Please adjust the start time, end time, or interview duration.', 'error');
        return;
      }

      // Use backend validation if available
      if (validationResult) {
        const effectiveTotal = validationResult.can_create_count + validationResult.truly_available_for_new;
        if (validationResult.can_create_count === 0 && validationResult.blocked?.length > 0) {
          showToastMsg(
            'All selected times are blocked by other jobs. Please choose different dates or times.',
            'error'
          );
          return;
        }
        if (activeCandidateCount > 0 && effectiveTotal < activeCandidateCount) {
          showToastMsg(
            `Not enough slots. ${effectiveTotal} effective slot${effectiveTotal !== 1 ? 's' : ''} ` +
            `available but need ${activeCandidateCount} for all candidates. Add more dates or extend the time window.`,
            'error'
          );
          return;
        }
      } else {
        // Fallback to client-side validation
        if (slotConflictInfo.newCount === 0 && slotConflictInfo.conflictCount > 0) {
          showToastMsg('All selected time slots are blocked. Please choose different dates or adjust the time range.', 'error');
          return;
        }

        const totalAvailableSlots = slotConflictInfo.newCount + jobAvailableCount;
        if (activeCandidateCount > 0 && totalAvailableSlots < activeCandidateCount) {
          showToastMsg(
            `Not enough slots. You will have ${totalAvailableSlots} slot${totalAvailableSlots !== 1 ? 's' : ''} ` +
            `but need ${activeCandidateCount} for all candidates. Add more dates or extend the time window.`,
            'error'
          );
          return;
        }
      }
    }

    // If using existing slots only, verify there are enough
    if (!needsNewSlots && activeCandidateCount > 0) {
      const trulyAvailable = validationResult
        ? validationResult.truly_available_for_new
        : jobAvailableCount;
      if (trulyAvailable < activeCandidateCount) {
        showToastMsg(
          `Only ${trulyAvailable} slot${trulyAvailable !== 1 ? 's' : ''} truly available but you have ` +
          `${activeCandidateCount} candidate${activeCandidateCount !== 1 ? 's' : ''} to schedule. ` +
          `Click "+ Add More Slots" to create additional slots.`,
          'error'
        );
        return;
      }
    }

    setSlotWarning(null);
    setError(null);
    setCurrentStep(3);
  };

  // Combined: Create session + Send invitations (Step 3 action)
  const handleScheduleAndSend = async () => {
    setIsScheduling(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const candidatesToSchedule = activeSelectedCandidates;
      if (candidatesToSchedule.length === 0) {
        showToastMsg('No candidates to schedule.', 'error');
        return;
      }

      // Create session
      const data = await aiAutomationApi.post('/interviews/schedule', {
        availability: {
          title: (availability.title || '').trim() || (jobTitle || '').trim() || '',
          dates: availability.dates,
          start_time: availability.startTime,
          end_time: availability.endTime,
          interview_duration: availability.interviewDuration,
          buffer_time: availability.bufferTime || 0,
        },
        candidates: candidatesToSchedule.map(c => ({
          name: c.name,
          email: c.email,
          phone: c.phone ? String(c.phone) : null,
          preferred_dates: c.preferredDates || null,
          preferred_start_time: c.preferredStartTime || null,
          preferred_end_time: c.preferredEndTime || null,
          source_candidate_id: String(c.sourceCandidateId || c.id || c.candidate_id || '') || null,
        })),
        search_id: searchId || null,
        interviewer_id: selectedInterviewer || null,
        company_id: resolvedCompanyId || null,
        source_session_id: sourceSessionId ? String(sourceSessionId) : null,
        interview_type: isAiMode ? 'ai' : 'manual',
        interview_setup_id: isAiMode ? (interviewSetup?.setup_id || interviewSetup?.interview_setup_id || null) : null,
        round_id: isAiMode ? (interviewSetup?.round_id || null) : null,
        ai_configuration_id: isAiMode ? (selectedAiConfig?.id || interviewSetup?.ai_configuration_id || null) : null,
      });

      if (!data.session_id) {
        showToastMsg('Invalid response from server. Missing session ID.', 'error');
        return;
      }

      setSessionId(data.session_id);
      const candidatesArray = data.candidates || [];
      setScheduledResult({ ...data, candidates: candidatesArray });

      // Send invitations to all created candidates
      const allCandidateIds = candidatesArray.map(c => c.candidate_id || c.id).filter(Boolean);

      if (allCandidateIds.length > 0) {
        const invData = await aiAutomationApi.post(`/interviews/sessions/${data.session_id}/send-invitations`, {
          candidate_ids: allCandidateIds,
        });

        setInvitationResults(invData);
        setSuccessMessage(
          `Session created and ${invData.sent_count} invitation${invData.sent_count !== 1 ? 's' : ''} sent.` +
          (invData.failed_count > 0 ? ` ${invData.failed_count} failed.` : '')
        );
      } else {
        setSuccessMessage('Session created but no candidates were assigned slots.');
      }

      setCurrentStep(4);

      if (onScheduleComplete) {
        onScheduleComplete({ session_id: data.session_id, ...data });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.detail || err.message || 'Failed to schedule interviews.';
      showToastMsg(detail, 'error');
    } finally {
      setIsScheduling(false);
    }
  };

  // Connect Google Calendar
  const handleConnectCalendar = async () => {
    try {
      setError(null);
      
      if (!isCalendarConfigured) {
        showToastMsg('Google Calendar credentials are not configured. Please contact your administrator.', 'error');
        return;
      }

      // Open the popup NOW, synchronously in the click handler, so the browser keeps the user-gesture
      // context. If we opened it after the auth-url await below, the popup would be blocked. We point
      // it at the auth URL once it resolves.
      const popup = window.open(
        'about:blank',
        'Google Calendar Authorization',
        'width=600,height=700,scrollbars=yes,resizable=yes'
      );
      if (!popup) {
        showToastMsg('Popup blocked. Please allow popups for this site and try again.', 'error');
        return;
      }

      let data;
      try {
        data = await aiAutomationApi.get('/interviews/calendar/auth-url');
      } catch (fetchErr) {
        try { popup.close(); } catch { /* ignore */ }
        throw fetchErr;
      }
      if (data?.auth_url) {
        popup.location.href = data.auth_url;
      } else {
        try { popup.close(); } catch { /* ignore */ }
        showToastMsg('Could not get the Google authorization link. Please try again.', 'error');
        return;
      }

      const messageListener = (event) => {
        if (event.data && event.data.type === 'GOOGLE_CALENDAR_CONNECTED') {
          if (event.data.success) {
            setIsCalendarConnected(true);
            setSuccessMessage('Google Calendar connected successfully!');
            setTimeout(() => setSuccessMessage(null), 5000);
          }
          window.removeEventListener('message', messageListener);
        }
      };
      
      window.addEventListener('message', messageListener);
      
      const checkPopup = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkPopup);
          window.removeEventListener('message', messageListener);
        }
      }, 1000);
      
    } catch (err) {
      showToastMsg(err.message || 'Failed to connect Google Calendar.', 'error');
    }
  };

  // ==================== STEPPER ====================
  const renderStepper = () => (
    <div className="flex items-center justify-between">
      {steps.map((step, index) => (
        <div key={step.id} className="flex items-center flex-1">
          <div className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                currentStep >= step.id
                  ? 'text-white'
                  : isDark
                    ? 'bg-gray-700 text-gray-400'
                    : 'bg-gray-200 text-gray-500'
              }`}
              style={currentStep >= step.id ? { backgroundColor: primaryColor } : {}}
            >
              {currentStep > step.id ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                step.id
              )}
            </div>
            <div className="ml-2 hidden sm:block">
              <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{step.label}</p>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{step.description}</p>
            </div>
          </div>
          {index < steps.length - 1 && (
            <div
              className={`flex-1 h-0.5 mx-4 ${
                currentStep > step.id
                  ? ''
                  : isDark
                    ? 'bg-gray-700'
                    : 'bg-gray-200'
              }`}
              style={currentStep > step.id ? { backgroundColor: primaryColor } : {}}
            />
          )}
        </div>
      ))}
    </div>
  );

  // ==================== MESSAGES ====================
  const renderMessages = () => (
    <>
      {/* Error messages now shown as Toast at bottom-right */}
      {successMessage && (
        <div className={`p-4 rounded-lg mb-4 flex items-start gap-3 ${
          isDark ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'
        }`}>
          <svg className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className={`text-sm ${isDark ? 'text-green-300' : 'text-green-700'}`}>{successMessage}</p>
        </div>
      )}
      {slotWarning && (
        <div className={`p-4 rounded-lg mb-4 flex items-start gap-3 ${
          isDark ? 'bg-amber-900/20 border border-amber-800' : 'bg-amber-50 border border-amber-200'
        }`}>
          <svg className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <p className={`text-sm ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>{slotWarning}</p>
        </div>
      )}
    </>
  );

  // ==================== DATE SELECTION MODAL ====================
  const renderDateModal = () => {
    if (!showDateModal) return null;

    const modalContent = (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" 
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowDateModal(false);
          }
        }}
        style={{ backdropFilter: 'blur(2px)' }}
      >
        <div 
          className={`rounded-lg shadow-2xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto ${
            isDark ? 'bg-gray-800' : 'bg-white'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Select Available Dates
              </h3>
              <button
                onClick={() => setShowDateModal(false)}
                className={`p-2 rounded transition-colors ${
                  isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mb-4">
              <Calendar
                onChange={handleDateSelect}
                value={selectedDates.length > 0 ? selectedDates[0] : null}
                selectRange={false}
                minDate={new Date()}
                className={`w-full border-0 ${isDark ? 'dark' : ''}`}
                tileClassName={({ date, view }) => {
                  if (view === 'month') {
                    const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                    const dateStr = formatDateToYYYYMMDD(localDate);
                    const isLocked = lockedDates.includes(dateStr);
                    const isSelected = availability.dates.includes(dateStr);
                    const hasGlobalSlots = globalOccupiedSlots.datesWithSlots?.includes(dateStr);
                    
                    if (isLocked) {
                      return 'react-calendar__tile--locked';
                    } else if (isSelected) {
                      return 'react-calendar__tile--active';
                    } else if (hasGlobalSlots) {
                      return 'react-calendar__tile--has-global-slots';
                    }
                  }
                  return null;
                }}
                tileContent={({ date, view }) => {
                  if (view === 'month') {
                    const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                    const dateStr = formatDateToYYYYMMDD(localDate);
                    const dateSlots = globalOccupiedSlots.byDate?.[dateStr];
                    if (dateSlots) {
                      const bookedCount = dateSlots.booked?.length || 0;
                      const availCount = dateSlots.available?.length || 0;
                      const total = bookedCount + availCount;
                      if (total > 0) {
                        return (
                          <div className="text-[8px] leading-tight mt-0.5" style={{ color: bookedCount > 0 ? '#ef4444' : '#f59e0b' }}>
                            {total} slot{total !== 1 ? 's' : ''}
                          </div>
                        );
                      }
                    }
                  }
                  return null;
                }}
              />
            </div>

            {availability.dates.length > 0 && (
              <div className={`mb-4 p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <p className={`text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Selected Dates ({availability.dates.length}):
                </p>
                <div className="flex flex-wrap gap-2">
                  {availability.dates.map((date) => {
                    const isLocked = lockedDates.includes(date);
                    return (
                      <span
                        key={date}
                        className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium ${
                          isLocked 
                            ? isDark
                              ? 'bg-gray-600 text-gray-300 border border-gray-500'
                              : 'bg-gray-200 text-gray-600 border border-gray-300'
                            : isDark
                              ? 'bg-indigo-900/50 text-indigo-300'
                              : 'bg-indigo-100 text-indigo-700'
                        }`}
                      >
                        {formatDateDisplay(date)}
                        {!isLocked && (
                          <button
                            onClick={() => {
                              const newDates = availability.dates.filter(d => d !== date);
                              setAvailability(prev => ({ ...prev, dates: newDates }));
                              setSelectedDates(prev => prev.filter(d => formatDateToYYYYMMDD(d) !== date));
                            }}
                            className={`ml-2 transition-colors ${
                              isDark ? 'text-indigo-400 hover:text-indigo-200' : 'text-indigo-600 hover:text-indigo-800'
                            }`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            <div className={`flex justify-end gap-3 pt-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => setShowDateModal(false)}
                className={`px-6 py-2.5 rounded font-medium text-sm transition-colors ${
                  isDark
                    ? 'bg-gray-700 text-white hover:bg-gray-600'
                    : 'text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    );

    return createPortal(modalContent, document.body);
  };

  // ==================== STEP 1: SETUP (Interviewer + Candidates) ====================
  const renderSetupStep = () => {
    if (loadingExistingSlots || loadingCandidatesStatus || interviewersLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <svg className="w-10 h-10 animate-spin mb-4" style={{ color: primaryColor }} fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Loading...</p>
        </div>
      );
    }

    const toggleCandidate = (email) => {
      const key = email.trim().toLowerCase();
      setSetupSelectedEmails(prev => {
        const next = new Set(prev);
        if (next.has(key)) { next.delete(key); } else { next.add(key); }
        return next;
      });
    };

    // Classify candidates into 3 groups
    const newCandidates = [];
    const bookedCandidates = [];
    const pendingCandidates = [];

    scheduledCandidates.forEach(c => {
      const email = (c.email || '').trim().toLowerCase();
      const status = candidateStatusMap[email];
      if (!status) {
        newCandidates.push(c); // Not in any session yet — new
      } else if (status.booking_status === 'booked') {
        bookedCandidates.push({ ...c, _status: status });
      } else {
        pendingCandidates.push({ ...c, _status: status }); // pending / sent link
      }
    });

    // Also include booked/pending candidates from backend that aren't in the props list
    Object.entries(candidateStatusMap).forEach(([email, status]) => {
      const inProps = scheduledCandidates.some(c => (c.email || '').trim().toLowerCase() === email);
      if (!inProps) {
        const entry = { name: status.name, email: status.email, _status: status };
        if (status.booking_status === 'booked') {
          bookedCandidates.push(entry);
        } else {
          pendingCandidates.push(entry);
        }
      }
    });

    const allActiveSelected = newCandidates.length > 0 &&
      newCandidates.every(c => setupSelectedEmails.has((c.email || '').trim().toLowerCase()));

    const toggleAll = () => {
      if (allActiveSelected) {
        setSetupSelectedEmails(new Set());
      } else {
        const all = new Set();
        newCandidates.forEach(c => {
          const email = (c.email || '').trim().toLowerCase();
          if (email) all.add(email);
        });
        setSetupSelectedEmails(all);
      }
    };

    // Slot stats
    const trulyAvailable = Math.max(0, jobAvailableCount - scheduledCounts.pending);
    const hasAnyExisting = jobTotalCount > 0 || scheduledCounts.booked > 0 || scheduledCounts.pending > 0;

    // Interviewer mismatch detection
    const previousInterviewerName = previousInterviewerId
      ? interviewers.find(i => i.id === previousInterviewerId)?.name
      : null;
    const isInterviewerMismatch = previousInterviewerId && selectedInterviewer && selectedInterviewer !== previousInterviewerId;

    // Collapsible section renderer
    const renderCollapsibleSection = (title, icon, badgeColor, items, isOpen, setIsOpen, renderItem) => {
      if (items.length === 0) return null;
      return (
        <div className={`rounded-lg border ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
          <button
            type="button"
            onClick={() => setIsOpen(v => !v)}
            className={`w-full flex items-center justify-between px-3 py-2.5 text-left transition-colors rounded-lg ${
              isDark ? 'hover:bg-gray-600/50' : 'hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center gap-2">
              {icon}
              <span className={`text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {title}
              </span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${badgeColor}`}>
                {items.length}
              </span>
            </div>
            <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''} ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {isOpen && (
            <div className={`px-3 pb-3 space-y-1.5 border-t ${isDark ? 'border-gray-600' : 'border-gray-100'}`}>
              <div className="pt-2 space-y-1.5">
                {items.map((item, idx) => renderItem(item, idx))}
              </div>
            </div>
          )}
        </div>
      );
    };

    // Render a read-only candidate row (booked / pending)
    const renderReadOnlyCandidate = (candidate, idx, statusBadge, badgeClass, avatarColor) => {
      const status = candidate._status;
      return (
        <div key={candidate.email || idx}
          className={`p-2.5 rounded-lg flex items-center gap-3 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
            style={{ backgroundColor: avatarColor }}>
            {(candidate.name || '?').charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className={`text-sm font-medium truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{candidate.name}</p>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${badgeClass}`}>
                {statusBadge}
              </span>
            </div>
            <p className={`text-[11px] truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{candidate.email}</p>
            <div className={`flex items-center gap-2 flex-wrap text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              {status?.scheduled_date && status.booking_status === 'booked' && (
                <span>{formatDateDisplay(status.scheduled_date)}{status.scheduled_time ? ` at ${formatTime(status.scheduled_time)}` : ''}</span>
              )}
              {status?.interviewer_name && (
                <span className="flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {status.interviewer_name}
                </span>
              )}
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-4">
        {isAiMode && (
          <AIInterviewConfigSelector
            companyId={resolvedCompanyId}
            sourceSessionId={sourceSessionId}
            value={selectedAiConfig}
            onChange={(config) => {
              if (!config) {
                setSelectedAiConfig(null);
                return;
              }
              if (interviewSetup && Number(interviewSetup.ai_configuration_id) !== Number(config?.id)) {
                showToastMsg('This job already has an AI setup. Future changes should update the saved setup intentionally.', 'error');
                return;
              }
              setSelectedAiConfig(config);
            }}
            isDark={isDark}
            primaryColor={primaryColor}
          />
        )}

        {/* Google Calendar Connection Banner */}
        {!isAiMode && !isCheckingCalendar && !isCalendarConnected && (
          <div className={`border-l-4 rounded-lg p-4 ${
            !isCalendarConfigured
              ? isDark ? 'bg-red-900/20 border-red-500' : 'bg-red-50 border-red-500'
              : isDark ? 'bg-amber-900/20 border-amber-500' : 'bg-amber-50 border-amber-500'
          }`}>
            <div className="flex items-start gap-3">
              <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                !isCalendarConfigured ? isDark ? 'text-red-400' : 'text-red-600' : isDark ? 'text-amber-400' : 'text-amber-600'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1">
                <p className={`text-sm font-semibold mb-1 ${!isCalendarConfigured ? isDark ? 'text-red-300' : 'text-red-900' : isDark ? 'text-amber-300' : 'text-amber-900'}`}>
                  {!isCalendarConfigured ? 'Google Calendar Not Configured' : 'Google Calendar Not Connected'}
                </p>
                <p className={`text-xs mb-2 ${!isCalendarConfigured ? isDark ? 'text-red-400' : 'text-red-700' : isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                  {!isCalendarConfigured ? 'Credentials not configured on server.' : 'Connect to schedule interviews and send calendar invites.'}
                </p>
                {isCalendarConfigured && (
                  <button onClick={handleConnectCalendar}
                    className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs font-medium transition-colors">
                    Connect Google Calendar
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Compact Job Overview (top) ── */}
        {hasAnyExisting && (
          <div className={`rounded-lg px-3 py-2 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`text-[11px] font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Job Status</span>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-50 text-green-700'}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />{scheduledCounts.booked} booked
                </span>
                <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-50 text-amber-700'}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />{scheduledCounts.pending} pending
                </span>
                <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />{jobAvailableCount} available
                </span>
                <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${isDark ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 inline-block" />{trulyAvailable} truly free
                </span>
              </div>
            </div>
            {scheduledCounts.pending > 0 && (
              <p className={`text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {jobAvailableCount} slots - {scheduledCounts.pending} pending = {trulyAvailable} truly available for new candidates
              </p>
            )}
          </div>
        )}

        {/* Interviewer Selection */}
        {!isAiMode && (
        <div className={`rounded-lg p-4 ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
          <div className="mb-2 flex items-center justify-between gap-3">
            <label className={`block text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Interviewer <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={navigateToInterviewerSettings}
              className="shrink-0 rounded px-3 py-1.5 text-xs font-medium text-white"
              style={{ backgroundColor: primaryColor }}
            >
              Create Interviewer
            </button>
          </div>
          {manualInterviewers.length === 0 ? (
            <div className={`border-l-4 rounded-lg p-3 ${isDark ? 'bg-amber-900/20 border-amber-500' : 'bg-amber-50 border-amber-500'}`}>
              <p className={`text-sm font-semibold mb-1 ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>No Interviewers Found</p>
              <p className={`text-xs ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>Go to Account Settings to add one.</p>
            </div>
          ) : (
            <select value={selectedInterviewer}
              onChange={(e) => { setSelectedInterviewer(e.target.value); setError(null); }}
              className={`w-full px-3.5 py-2.5 border rounded-lg focus:ring-2 focus:border-indigo-500 text-sm ${
                !selectedInterviewer
                  ? isDark ? 'bg-gray-700 border-amber-500 text-gray-400' : 'bg-white border-amber-400 text-gray-400'
                  : isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`} style={{ '--tw-ring-color': primaryColor }}>
              {!selectedInterviewer && <option value="" disabled>— Select Interviewer —</option>}
              {manualInterviewers.map((i) => (
                <option key={i.id} value={i.id}>{i.name}{i.designation ? ` (${i.designation})` : ''} — {i.email}</option>
              ))}
            </select>
          )}

          {/* Interviewer mismatch warning */}
          {isInterviewerMismatch && (
            <div className={`mt-2 flex items-start gap-2 p-2.5 rounded-lg border ${isDark ? 'bg-amber-900/15 border-amber-700' : 'bg-amber-50 border-amber-200'}`}>
              <svg className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className={`text-xs ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                Previous bookings used <span className="font-semibold">{previousInterviewerName || 'a different interviewer'}</span>. Changing the interviewer means new slots will be created under a different interviewer.
              </p>
            </div>
          )}
        </div>
        )}

        {/* ── NEW CANDIDATES (selectable, always visible) ── */}
        <div className={`rounded-lg p-4 ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                New Candidates to Schedule <span className="text-red-500">*</span>
              </h3>
              <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {setupSelectedEmails.size} of {newCandidates.length} selected
              </p>
            </div>
            {newCandidates.length > 0 && (
              <button type="button" onClick={toggleAll}
                className={`text-xs font-medium px-2.5 py-1 rounded transition-colors ${
                  allActiveSelected ? isDark ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600' : 'text-white'
                }`} style={!allActiveSelected ? { backgroundColor: primaryColor } : {}}>
                {allActiveSelected ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>

          {newCandidates.length === 0 ? (
            <div className={`text-center py-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              <p className="text-sm">No new candidates to schedule</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[280px] overflow-y-auto">
              {newCandidates.map((candidate, index) => {
                const email = (candidate.email || '').trim().toLowerCase();
                const isSelected = setupSelectedEmails.has(email);
                return (
                  <div key={email || index}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      isSelected
                        ? isDark ? 'bg-indigo-900/30 border-indigo-500' : 'bg-indigo-50 border-indigo-400'
                        : isDark ? 'bg-gray-700 border-gray-600 hover:border-gray-500' : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => toggleCandidate(email)}>
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'text-white' : isDark ? 'border-2 border-gray-500' : 'border-2 border-gray-300'
                      }`} style={isSelected ? { backgroundColor: primaryColor } : {}}>
                        {isSelected && (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                        style={{ backgroundColor: isSelected ? primaryColor : (isDark ? '#4b5563' : '#9ca3af') }}>
                        {(candidate.name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{candidate.name}</p>
                        <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{candidate.email}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── PENDING CANDIDATES (collapsible) ── */}
        {renderCollapsibleSection(
          'Pending — Sent link, awaiting booking',
          <svg className={`w-4 h-4 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>,
          isDark ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-700',
          pendingCandidates,
          showPendingSection,
          setShowPendingSection,
          (c, idx) => renderReadOnlyCandidate(c, idx, 'Pending', isDark ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-700', '#f59e0b')
        )}

        {/* ── BOOKED CANDIDATES (collapsible) ── */}
        {renderCollapsibleSection(
          'Booked — Interview confirmed',
          <svg className={`w-4 h-4 ${isDark ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>,
          isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700',
          bookedCandidates,
          showBookedSection,
          setShowBookedSection,
          (c, idx) => renderReadOnlyCandidate(c, idx, 'Booked', isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700', '#10b981')
        )}
      </div>
    );
  };

  // ==================== STEP 2: AVAILABILITY ====================
  const renderAvailabilityStep = () => {
    if (loadingExistingSlots || loadingCandidatesStatus || loadingGlobalSlots) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <svg className="w-10 h-10 animate-spin mb-4" style={{ color: primaryColor }} fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Loading previous session data...</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* ── Existing-slots banner ───────────────────────────────────── */}
        {jobAvailableCount > 0 && (() => {
          const _activeCandCount = activeSelectedCandidates.length;
          const _enough = jobAvailableCount >= _activeCandCount || _activeCandCount === 0;
          return (
            <div className={`rounded-lg p-4 border ${
              _enough
                ? isDark ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'
                : isDark ? 'bg-amber-900/20 border-amber-700' : 'bg-amber-50 border-amber-200'
            }`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5">
                  <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${_enough ? isDark ? 'text-green-400' : 'text-green-600' : isDark ? 'text-amber-400' : 'text-amber-600'}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {_enough
                      ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    }
                  </svg>
                  <div>
                    <p className={`text-sm font-semibold ${_enough ? isDark ? 'text-green-300' : 'text-green-800' : isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                      {_enough
                        ? `${jobAvailableCount} available slot${jobAvailableCount !== 1 ? 's' : ''} — ready to use`
                        : `Only ${jobAvailableCount} slot${jobAvailableCount !== 1 ? 's' : ''} available — need ${_activeCandCount - jobAvailableCount} more`
                      }
                    </p>
                    <p className={`text-xs mt-0.5 ${_enough ? isDark ? 'text-green-400' : 'text-green-600' : isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                      {_enough
                        ? 'Candidates will be assigned to existing slots. No new slots needed.'
                        : 'Add more slots below so every candidate gets a booking link.'
                      }
                    </p>
                    {/* Slot / candidate breakdown */}
                    {_activeCandCount > 0 && (
                      <p className={`text-xs mt-1 font-medium ${_enough ? isDark ? 'text-green-300' : 'text-green-700' : isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                        {_activeCandCount} candidate{_activeCandCount !== 1 ? 's' : ''} to schedule
                        {_enough && jobAvailableCount - _activeCandCount >= 0
                          ? ` · ${jobAvailableCount - _activeCandCount} slot${jobAvailableCount - _activeCandCount !== 1 ? 's' : ''} will remain after`
                          : ''
                        }
                      </p>
                    )}
                  </div>
                </div>
                {/* Toggle: add more slots */}
                <button
                  type="button"
                  onClick={() => setShowAddMoreSlots(v => !v)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                    showAddMoreSlots
                      ? isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-600'
                      : isDark ? 'border-indigo-500 text-indigo-400 hover:bg-indigo-900/30' : 'border-indigo-400 text-indigo-700 hover:bg-indigo-50'
                  }`}
                >
                  {showAddMoreSlots ? '✕ Cancel' : '+ Add More Slots'}
                </button>
              </div>
            </div>
          );
        })()}

        {/* ── Slot creation section (hidden when existing slots are enough) ── */}
        {(showAddMoreSlots || jobAvailableCount === 0) && (
        <>
        <div className={`rounded-lg p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
          <h3 className={`text-base font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {jobAvailableCount > 0 ? 'Add More Slots' : 'Set Your Availability'}
          </h3>
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {jobAvailableCount > 0 ? 'Pick new dates and times to expand the slot pool for this job.' : 'Configure when you\'re available for interviews'}
          </p>
        </div>

        <div>
          <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Available Dates <span className="text-red-500">*</span>
          </label>
          <button
            type="button"
            onClick={() => setShowDateModal(true)}
            className={`w-full px-3.5 py-2.5 border rounded-lg transition-colors text-left flex items-center justify-between text-sm ${
              isDark
                ? 'bg-gray-700 border-gray-600 hover:border-indigo-500 text-gray-300'
                : 'bg-white border-gray-300 hover:border-indigo-500 hover:bg-indigo-50 text-gray-700'
            }`}
          >
            <span>
              {availability.dates.length > 0 
                ? `${availability.dates.length} date${availability.dates.length !== 1 ? 's' : ''} selected`
                : 'Click to select dates'
              }
            </span>
            <svg className={`w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </button>
          {availability.dates.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {availability.dates.map((date) => (
                <span
                  key={date}
                  className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium ${
                    isDark ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-700'
                  }`}
                >
                  {formatDateDisplay(date)}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <InputBox
            label="Start Time"
            type="time"
            value={availability.startTime}
            onChange={(e) => {
              setAvailability(prev => ({ ...prev, startTime: e.target.value }));
              setError(null);
            }}
            required
          />

          <InputBox
            label="End Time"
            type="time"
            value={availability.endTime}
            onChange={(e) => {
              setAvailability(prev => ({ ...prev, endTime: e.target.value }));
              setUserManuallySetEndTime(true);
              setError(null);
            }}
            required
          />

          <div>
            <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Duration <span className="text-red-500">*</span>
            </label>
            <select
              value={availability.interviewDuration}
              onChange={(e) => {
                setAvailability(prev => ({ ...prev, interviewDuration: parseInt(e.target.value) }));
                setError(null);
              }}
              className={`w-full px-3.5 py-2.5 border rounded-lg focus:ring-2 focus:border-indigo-500 text-sm ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              {durationOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Buffer Time Between Interviews
          </label>
          <select
            value={availability.bufferTime}
            onChange={(e) => setAvailability(prev => ({ ...prev, bufferTime: parseInt(e.target.value) }))}
            className={`w-full px-3.5 py-2.5 border rounded-lg focus:ring-2 focus:border-indigo-500 text-sm ${
              isDark 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value={0}>No buffer</option>
            <option value={5}>5 minutes</option>
            <option value={10}>10 minutes</option>
            <option value={15}>15 minutes</option>
            <option value={30}>30 minutes</option>
          </select>
        </div>

        {/* Slot Summary */}
        {availability.dates.length > 0 && (
          <div className={`p-4 rounded-lg ${isDark ? 'bg-indigo-900/20 border border-indigo-800' : 'bg-indigo-50 border border-indigo-200'}`}>
            <div className="flex items-center gap-2 mb-2">
              <svg className={`w-5 h-5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className={`text-sm font-semibold ${isDark ? 'text-indigo-200' : 'text-indigo-800'}`}>
                Slot Preview
              </span>
            </div>

            {/* Summary Stats */}
            <div className={`flex items-center gap-3 mb-3 text-xs ${isDark ? 'text-indigo-300' : 'text-indigo-700'}`}>
              {slotConflictInfo.newCount > 0 && (
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700'}`}>
                  <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                  {slotConflictInfo.newCount} new
                </span>
              )}
              {jobAvailableCount > 0 && (
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                  <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
                  {jobAvailableCount} available
                </span>
              )}
              {jobBookedCount > 0 && (
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${isDark ? 'bg-orange-900/30 text-orange-300' : 'bg-orange-50 text-orange-700'}`}>
                  <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" />
                  {jobBookedCount} booked
                </span>
              )}
              {(slotConflictInfo.newCount > 0 || jobAvailableCount > 0) && (
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${isDark ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`}>
                  <span className="w-2 h-2 rounded-full bg-indigo-500 inline-block" />
                  {slotConflictInfo.newCount + jobAvailableCount + jobBookedCount} total
                </span>
              )}
              {slotConflictInfo.newCount === 0 && slotConflictInfo.conflictCount > 0 && (
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-600'}`}>
                  <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
                  0 new — all times blocked
                </span>
              )}
              {slotConflictInfo.newCount === 0 && slotConflictInfo.conflictCount === 0 && jobAvailableCount === 0 && (
                <span className={`text-xs ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                  No slots — select dates and times above
                </span>
              )}
            </div>

            {/* Live slot counter vs candidate count */}
            {activeSelectedCandidates.length > 0 && maxCandidates > 0 && (() => {
              const _activeCandCount = activeSelectedCandidates.length;
              const _totalSlots = validationResult
                ? validationResult.can_create_count + validationResult.truly_available_for_new
                : slotConflictInfo.newCount + jobAvailableCount;
              const _insufficient = _activeCandCount > 0 && _totalSlots < _activeCandCount;
              return (
                <p className={`text-xs font-medium mb-2 ${_insufficient
                  ? (isDark ? 'text-amber-400' : 'text-amber-600')
                  : (isDark ? 'text-green-400' : 'text-green-700')
                }`}>
                  {_insufficient ? 'Warning:' : 'OK:'} {_totalSlots} slot{_totalSlots !== 1 ? 's' : ''} for {_activeCandCount} candidate{_activeCandCount !== 1 ? 's' : ''}
                  {_insufficient && ' — add more dates or extend time window'}
                </p>
              );
            })()}

            {/* Slot grid grouped by date — only show new slots */}
            {slotConflictInfo.newCount > 0 && (
              <div className="space-y-2">
                {Object.entries(
                  slotConflictInfo.newSlots.reduce((acc, slot) => {
                    if (!acc[slot.date]) acc[slot.date] = [];
                    acc[slot.date].push(slot);
                    return acc;
                  }, {})
                ).map(([date, slots]) => (
                  <div key={date}>
                    <p className={`text-xs font-semibold mb-1 ${isDark ? 'text-indigo-300' : 'text-indigo-700'}`}>
                      {formatDateDisplay(date)}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {slots.map((slot, idx) => (
                        <span
                          key={idx}
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${isDark ? 'bg-indigo-800/50 text-indigo-200' : 'bg-indigo-100 text-indigo-700'}`}
                        >
                          {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Backend Validation Results */}
        {validationLoading && (
          <div className={`p-3 rounded-lg flex items-center gap-3 ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
            <svg className="w-4 h-4 animate-spin" style={{ color: primaryColor }} fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Validating slots...</span>
          </div>
        )}
        {validationResult && !validationLoading && (
          <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
            <div className="flex items-center gap-2 mb-3">
              <svg className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className={`text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Slot Validation
              </span>
            </div>
            <div className={`flex items-center gap-3 flex-wrap text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              {validationResult.can_create_count > 0 && (
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700'}`}>
                  <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                  {validationResult.can_create_count} new
                </span>
              )}
              {validationResult.truly_available_for_new > 0 && (
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                  <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
                  {validationResult.truly_available_for_new} reusable
                </span>
              )}
              {validationResult.blocked?.length > 0 && (
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-600'}`}>
                  <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
                  {validationResult.blocked.length} blocked
                </span>
              )}
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${isDark ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`}>
                <span className="w-2 h-2 rounded-full bg-indigo-500 inline-block" />
                {validationResult.can_create_count + validationResult.truly_available_for_new} effective total
              </span>
            </div>
            {/* Blocked slot details */}
            {validationResult.blocked?.length > 0 && (
              <div className="mt-3 space-y-1">
                <p className={`text-xs font-medium mb-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                  Blocked by other jobs:
                </p>
                {validationResult.blocked.slice(0, 8).map((b, idx) => (
                  <div key={idx} className={`flex items-center gap-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    <span className="w-2 h-2 rounded-full bg-red-500 inline-block flex-shrink-0" />
                    <span>{formatDateDisplay(b.date)} {formatTime(b.time)}</span>
                    <span className={`${isDark ? 'text-gray-500' : 'text-gray-400'}`}>— {b.blocking_job}</span>
                  </div>
                ))}
                {validationResult.blocked.length > 8 && (
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    +{validationResult.blocked.length - 8} more blocked slots
                  </p>
                )}
              </div>
            )}
            {/* Same-job occupied ranges */}
            {validationResult.existing_overlaps?.length > 0 && (() => {
              // Compute occupied range per date
              const rangeByDate = {};
              validationResult.existing_overlaps.forEach(ov => {
                const [eh, em] = (ov.existing_time || '00:00').split(':').map(Number);
                const startMin = eh * 60 + em;
                const endMin = startMin + (ov.existing_duration || 30);
                if (!rangeByDate[ov.date]) {
                  rangeByDate[ov.date] = { min: startMin, max: endMin };
                } else {
                  rangeByDate[ov.date].min = Math.min(rangeByDate[ov.date].min, startMin);
                  rangeByDate[ov.date].max = Math.max(rangeByDate[ov.date].max, endMin);
                }
              });
              const minsToTime = (m) => {
                const h = Math.floor(m / 60);
                const mi = m % 60;
                return formatTime(`${String(h).padStart(2,'0')}:${String(mi).padStart(2,'0')}`);
              };
              return (
                <div className="mt-3">
                  <p className={`text-xs font-medium mb-1.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                    This job already occupies:
                  </p>
                  {Object.keys(rangeByDate).sort().map(date => (
                    <div key={date} className={`flex items-center gap-2 text-xs mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      <span className="w-2 h-2 rounded-full bg-blue-500 inline-block flex-shrink-0" />
                      <span>{formatDateDisplay(date)}: {minsToTime(rangeByDate[date].min)} – {minsToTime(rangeByDate[date].max)}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* Interviewer's Existing Slots — compact summary */}
        {selectedInterviewer && loadingGlobalSlots && (
          <div className={`p-4 rounded-lg flex items-center gap-3 ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
            <svg className="w-4 h-4 animate-spin" style={{ color: primaryColor }} fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading interviewer's schedule...</span>
          </div>
        )}
        {selectedInterviewer && !loadingGlobalSlots && (
          <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <svg className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span className={`text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Blocked Times (Other Jobs)
                </span>
              </div>
              {globalOccupiedSlots.totalCount > 0 ? (
                <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
                    {globalOccupiedSlots.blockedCount} blocked
                  </span>
                </div>
              ) : (
                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  No conflicts with other jobs
                </span>
              )}
            </div>
            {globalOccupiedSlots.totalCount > 0 ? (
              <>
                <p className={`text-xs mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  These times are reserved by other jobs. Overlapping slots cannot be created.
                </p>
                {globalOccupiedSlots.datesWithSlots?.length > 0 && (
              <div className="space-y-2">
                {globalOccupiedSlots.datesWithSlots.sort().map(date => {
                  const dateSlots = globalOccupiedSlots.byDate[date];
                  const blockedTimes = dateSlots?.blocked || [];
                  return (
                    <div key={date} className={`flex items-center gap-2 py-1 ${isDark ? 'border-b border-gray-700' : 'border-b border-gray-100'} last:border-0`}>
                      <span className={`text-xs font-medium min-w-[90px] ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {formatDateDisplay(date)}
                      </span>
                      <div className="flex items-center gap-1 flex-1 flex-wrap">
                        {blockedTimes.sort().map((time, idx) => (
                          <span
                            key={`bl-${idx}`}
                            className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-600'}`}
                          >
                            {formatTime(time)}
                          </span>
                        ))}
                      </div>
                      <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {blockedTimes.length} blocked
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
              </>
            ) : (
              <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                No conflicts — this interviewer has no slots in other jobs.
              </p>
            )}
          </div>
        )}
        </> /* close: (showAddMoreSlots || jobAvailableCount === 0) */
        )}
      </div>
    );
  };

  // ==================== STEP 3: REVIEW & SEND ====================
  const renderReviewStep = () => {
    const selectedInterviewerObj = manualInterviewers.find(i => i.id === selectedInterviewer);
    const candidatesToSchedule = activeSelectedCandidates;
    const needsNewSlots = showAddMoreSlots || jobAvailableCount === 0;
    const effectiveSlots = validationResult
      ? validationResult.can_create_count + validationResult.truly_available_for_new
      : (needsNewSlots ? slotConflictInfo.newCount : 0) + jobAvailableCount;

    return (
      <div className="space-y-4">
        <div className={`rounded-lg p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
          <h3 className={`text-base font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>Review & Confirm</h3>
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Review your selections and schedule interviews.
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Interviewer */}
          <div className={`p-3 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
            <p className={`text-[10px] uppercase font-semibold tracking-wider mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Interviewer</p>
            <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {selectedInterviewerObj?.name || 'Selected'}
            </p>
            {selectedInterviewerObj?.email && (
              <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {selectedInterviewerObj.email}
              </p>
            )}
          </div>
          {/* Candidates */}
          <div className={`p-3 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
            <p className={`text-[10px] uppercase font-semibold tracking-wider mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Candidates</p>
            <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {candidatesToSchedule.length} candidate{candidatesToSchedule.length !== 1 ? 's' : ''}
            </p>
          </div>
          {/* Slots */}
          <div className={`p-3 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
            <p className={`text-[10px] uppercase font-semibold tracking-wider mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Available Slots</p>
            <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {effectiveSlots} slot{effectiveSlots !== 1 ? 's' : ''}
            </p>
            {needsNewSlots && availability.dates.length > 0 && (
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {availability.dates.length} date{availability.dates.length !== 1 ? 's' : ''} · {formatTime(availability.startTime)} - {formatTime(availability.endTime)}
              </p>
            )}
          </div>
        </div>

        {/* Schedule details */}
        {needsNewSlots && availability.dates.length > 0 && (
          <div className={`p-3 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
            <p className={`text-xs font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Schedule Details</p>
            <div className={`grid grid-cols-2 gap-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              <div>Duration: <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{availability.interviewDuration} min</span></div>
              <div>Buffer: <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{availability.bufferTime || 0} min</span></div>
              <div className="col-span-2">
                Dates: <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {availability.dates.map(d => formatDateDisplay(d)).join(', ')}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Candidate list */}
        <div className={`rounded-lg border ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
          <div className={`px-3 py-2 border-b ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            <p className={`text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Candidates to Schedule ({candidatesToSchedule.length})
            </p>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-[240px] overflow-y-auto">
            {candidatesToSchedule.map((candidate, idx) => (
              <div key={candidate.email || idx} className={`px-3 py-2.5 flex items-center gap-3 ${isDark ? '' : ''}`}>
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                  style={{ backgroundColor: primaryColor }}
                >
                  {(candidate.name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{candidate.name}</p>
                  <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{candidate.email}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Ready indicator */}
        <div className={`p-3 rounded-lg flex items-center gap-2 ${
          isDark ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'
        }`}>
          <svg className={`w-5 h-5 ${isDark ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className={`text-sm font-medium ${isDark ? 'text-green-300' : 'text-green-700'}`}>
            Ready to schedule {candidatesToSchedule.length} candidate{candidatesToSchedule.length !== 1 ? 's' : ''} and send booking invitations.
          </p>
        </div>
      </div>
    );
  };

  // ==================== STEP 4: CONFIRMATION ====================
  const renderConfirmationStep = () => (
    <div className="space-y-6">
      <div className="text-center py-8">
        <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
          <svg className="w-8 h-8" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Invitations Sent!
        </h3>
        <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          {invitationResults?.sent_count || 0} invitation{(invitationResults?.sent_count || 0) !== 1 ? 's' : ''} sent successfully.
          {invitationResults?.failed_count > 0 && (
            <span className={isDark ? 'text-red-400' : 'text-red-600'}>
              {' '}{invitationResults.failed_count} failed.
            </span>
          )}
        </p>
      </div>
    </div>
  );

  // ==================== MAIN RENDER ====================
  if (!isOpen) return null;

  // No Interviewer Warning Modal
  const noInterviewerModal = showNoInterviewerModal ? createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50"
      style={{ backdropFilter: 'blur(2px)' }}
      onClick={() => setShowNoInterviewerModal(false)}
    >
      <div
        className={`w-full max-w-sm rounded-2xl shadow-xl overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-white'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <svg className="w-7 h-7 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>No Interviewer Selected</h3>
          <p className={`text-sm mb-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            You need to select an interviewer before scheduling interviews. Please go to
            <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}> Account Settings </span>
            to add an interviewer first.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => {
                setShowNoInterviewerModal(false);
                navigateToInterviewerSettings();
              }}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
              style={{ backgroundColor: primaryColor }}
            >
              Create Interview
            </button>
            <button
              onClick={() => setShowNoInterviewerModal(false)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isDark ? 'text-gray-300 bg-gray-700 hover:bg-gray-600' : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
              }`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  const modalContent = (
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 ${isDark ? 'dark' : ''}`}
      style={{ backdropFilter: 'blur(2px)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div 
        className={`relative rounded-lg shadow-xl w-full max-w-4xl h-[85vh] overflow-hidden flex flex-col z-10 ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="flex-shrink-0 flex items-center justify-between px-5 py-3 text-white"
          style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
        >
          <div>
            <h2 className="text-lg font-semibold">{jobTitle || 'Schedule Interviews'}</h2>
            <p className="text-xs opacity-80 mt-0.5">
              {isAiMode
                ? 'AI interview scheduling'
                : selectedInterviewer
                ? `Interviewer: ${manualInterviewers.find(i => i.id === selectedInterviewer)?.name || 'Selected'}`
                : 'Create interview slots for candidates'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/10 rounded transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Stepper */}
        <div className={`flex-shrink-0 px-5 pt-3 pb-3 border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          {interviewMode ? renderStepper() : (
            <div className={`text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Interview type</div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0 interview-scheduler-content">
          <div className="p-6 pt-4">
            {renderMessages()}
            
            <div className="mt-4">
              {setupLoading && !interviewMode ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <svg className="mb-4 h-10 w-10 animate-spin" style={{ color: primaryColor }} fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Checking interview setup...</p>
                </div>
              ) : !interviewMode ? (
                <InterviewTypeChoice
                  isDark={isDark}
                  primaryColor={primaryColor}
                  onChoose={handleChooseInterviewMode}
                  canUseAi={canUseAiInterview}
                  canUseManual={canUseManualInterview}
                />
              ) : (
                <>
                  {currentStep === 1 && renderSetupStep()}
                  {currentStep === 2 && renderAvailabilityStep()}
                  {currentStep === 3 && renderReviewStep()}
                  {currentStep === 4 && renderConfirmationStep()}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`flex-shrink-0 border-t px-5 py-3 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          {!interviewMode && (
            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={onClose}
                className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                  isDark ? 'border border-gray-600 text-gray-300 hover:bg-gray-700' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
            </div>
          )}
          {/* Step 1: Setup → Next */}
          {interviewMode && currentStep === 1 && (
            <div className="flex items-center justify-between">
              {enableInterviewTypeChoice && !interviewSetup ? (
                <button
                  type="button"
                  onClick={() => {
                    setInterviewMode('');
                    setSelectedAiConfig(null);
                    setSelectedInterviewer('');
                    setError(null);
                  }}
                  className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                    isDark ? 'border border-gray-600 text-gray-300 hover:bg-gray-700' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Change interview type
                </button>
              ) : <span />}
              <button
                onClick={handleSetupNext}
                disabled={setupLoading || setupSelectedEmails.size === 0 || (!isAiMode && (!selectedInterviewer || manualInterviewers.length === 0)) || (isAiMode && !selectedAiConfig?.id && !interviewSetup?.ai_configuration_id)}
                className="px-6 py-2.5 text-white rounded-lg hover:opacity-90 font-medium text-sm transition-colors flex items-center gap-2 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: primaryColor }}
              >
                {setupLoading ? 'Preparing...' : 'Next: Availability →'}
              </button>
            </div>
          )}

          {/* Step 2: Availability → Back / Next */}
          {interviewMode && currentStep === 2 && (
            <div className="flex items-center justify-between">
              <button
                onClick={() => setCurrentStep(1)}
                className={`px-5 py-2.5 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 ${
                  isDark
                    ? 'bg-gray-700 text-white hover:bg-gray-600'
                    : 'bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-50'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
              <div className="flex items-center gap-3">
                {jobAvailableCount > 0 && !showAddMoreSlots && (
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {jobAvailableCount} slot{jobAvailableCount !== 1 ? 's' : ''} available
                  </p>
                )}
                <button
                  onClick={handleAvailabilityNext}
                  className="px-6 py-2.5 text-white rounded-lg hover:opacity-90 font-medium text-sm transition-colors flex items-center gap-2 shadow-sm"
                  style={{ backgroundColor: primaryColor }}
                >
                  {jobAvailableCount > 0 && !showAddMoreSlots ? 'Use Existing Slots →' : 'Next: Review →'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Review → Back / Schedule & Send */}
          {interviewMode && currentStep === 3 && (
            <div className="flex items-center justify-between">
              <button
                onClick={() => setCurrentStep(2)}
                disabled={isScheduling}
                className={`px-5 py-2.5 rounded-lg font-medium text-sm disabled:opacity-50 transition-colors flex items-center gap-2 ${
                  isDark
                    ? 'bg-gray-700 text-white hover:bg-gray-600'
                    : 'bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-50'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
              <button
                onClick={handleScheduleAndSend}
                disabled={isScheduling || activeSelectedCandidates.length === 0}
                className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 shadow-sm"
              >
                {isScheduling ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Scheduling...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Schedule & Send Invitations
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step 4: Done */}
          {interviewMode && currentStep === 4 && (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={onClose}
                className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                  isDark
                    ? 'border border-gray-600 text-gray-300 hover:bg-gray-700'
                    : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
      
      {renderDateModal()}
    </div>
  );

  return (
    <>
      {noInterviewerModal}
      {createPortal(modalContent, document.body)}
      {/* Toast for error messages — portalled to body so it renders above the modal backdrop */}
      {createPortal(
        <Toast
          message={toastState.message}
          type={toastState.type}
          visible={toastState.visible}
          onClose={() => setToastState(prev => ({ ...prev, visible: false }))}
          duration={5000}
        />,
        document.body
      )}
    </>
  );
};

export default InterviewScheduler;
